// Set & Ledger design tokens: a cool slate neutral ramp, one deep-teal brand
// accent, and a restrained set of semantic tints for status badges.
// The app's signature tint lives in `src/brand.ts` and is applied through
// <BrandProvider> (which sets --primary / --primary-dark / --primary-focus).

export type TintKey =
  | "peach"
  | "mint"
  | "sky"
  | "lavender"
  | "butter"
  | "rose"
  | "sage"
  | "sand";

export interface Tint {
  bg: string;
  stroke: string;
  ink: string;
}

export const B = {
  canvas: "#f6f7f9", // page background
  canvas2: "#eef1f4", // subtle fills, hover rows
  paper: "#ffffff", // cards, tables, inputs
  ink: "#0f172a", // primary text
  ink2: "#475569", // secondary text
  muted: "#64748b", // tertiary text, labels
  line: "#e2e8f0", // hairline borders
  accent: "#0f766e", // brand teal
  accentSoft: "#e6f4f1",
  navy: "#0b1220", // sidebar
  // Semantic tints (badge bg / border / text). Names are historical keys.
  tints: {
    peach: { bg: "#fff7ed", stroke: "#fed7aa", ink: "#c2410c" }, // orange
    mint: { bg: "#ecfdf5", stroke: "#a7f3d0", ink: "#047857" }, // green
    sky: { bg: "#eff6ff", stroke: "#bfdbfe", ink: "#1d4ed8" }, // blue
    lavender: { bg: "#f5f3ff", stroke: "#ddd6fe", ink: "#6d28d9" }, // violet
    butter: { bg: "#fffbeb", stroke: "#fde68a", ink: "#b45309" }, // amber
    rose: { bg: "#fef2f2", stroke: "#fecaca", ink: "#b91c1c" }, // red
    sage: { bg: "#e6f4f1", stroke: "#99d5c9", ink: "#0f766e" }, // brand teal
    sand: { bg: "#f1f5f9", stroke: "#e2e8f0", ink: "#475569" }, // neutral
  } satisfies Record<TintKey, Tint>,
} as const;

export const bFonts = {
  display: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  sans: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  mono: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
} as const;

// Type-size scale (px).
export const bText = {
  micro: 11, // uppercase section labels
  xs: 12, // fine print
  sm: 13, // secondary text, badges
  md: 14, // body, nav items, buttons
  lg: 15, // card titles
  xl: 18, // section titles
  display: 24, // page titles
} as const;

export type BTextKey = keyof typeof bText;

const TINT_KEYS: TintKey[] = Object.keys(B.tints) as TintKey[];

// Deterministic tint for an arbitrary name (stable per-name accent).
export function tintFor(name: string): Tint {
  if (!name) return B.tints.lavender;
  const idx = (name.charCodeAt(0) + name.length * 3) % TINT_KEYS.length;
  return B.tints[TINT_KEYS[idx]];
}

// Darken a hex color by a fraction (0..1); derives --primary-dark from the tint.
export function darken(hex: string, amount = 0.18): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const r = Math.max(0, Math.round(((n >> 16) & 0xff) * (1 - amount)));
  const g = Math.max(0, Math.round(((n >> 8) & 0xff) * (1 - amount)));
  const b = Math.max(0, Math.round((n & 0xff) * (1 - amount)));
  return "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("");
}
